import { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { useCreatePortalForm, usePartnerData, usePortalForms, useUpdatePortalForm } from "@/hooks/useLocalStorage";
import { parseApiErrorMessage } from "@/hooks/parseApiErrorMessage";
import { AlertDialog } from "@/widgets/AlertDailog";

export type CreateFormValues = {
    FormPartnerType: string;
    FormName: string;
    ExistingFormId?: number;
};

type CreateFormDialogProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    initialData?: any;
    fromType: string
};

export function CreateFormDialog({ open, onOpenChange, initialData, fromType }: CreateFormDialogProps) {
    const [documentValidationError, setDocumentValidationError] = useState<string>("");
    const createPortalForm = useCreatePortalForm();
    const updatePortalForm = useUpdatePortalForm();
    const [alertOpen, setAlertOpen] = useState(false);
    const [alertMessage, setAlertMessage] = useState("");
    const [reqStatus, setReqStatus] = useState(false);

    const { data: existingForms, isLoading } = usePortalForms(
        1,
        10,
        fromType
    );

    const { data: partners } = usePartnerData(
        1,
        10,
    );
    console.log("partners", partners)
    const form = useForm<CreateFormValues>({
        defaultValues: {
            FormPartnerType: "",
            FormName: "",
            ExistingFormId: undefined,
        },
    });

    const { control, register, handleSubmit, reset, setValue, watch, getValues } = form;

    useEffect(() => {
        if (open && initialData) {
            console.log("Initial Data:", initialData);
            console.log("FormPartnerType:", initialData.FormPartnerType);
            console.log("Available Partners:", partners?.data?.items);

            const partnerTypeValue = initialData.FormPartnerType
                ? initialData.FormPartnerType.toString()
                : "";

            reset({
                FormName: initialData.FormName || "",
                FormPartnerType: partnerTypeValue,
            });
        } else if (open && !initialData) {
            reset({
                FormPartnerType: "",
                FormName: "",
                ExistingFormId: undefined,
            });
        }
        setDocumentValidationError("");
    }, [open, initialData, reset, partners]);

    const onSubmit = (data: CreateFormValues) => {
        if (initialData) {
            updatePortalForm.mutateAsync({
                Id: initialData.FormId,
                data,
                formType: fromType
            });
        } else {
            createPortalForm.mutateAsync({ data, formType: fromType });
        }
    };

    useEffect(() => {
        if (createPortalForm.isSuccess === true) {
            setReqStatus(createPortalForm.isSuccess);
            setAlertMessage(`Form Created Successfully!`);
            setAlertOpen(true);
            onOpenChange(false);
            createPortalForm.reset();
        } else if (createPortalForm.isError) {
            const raw = createPortalForm.error as Error;
            const parsedMsg = parseApiErrorMessage(raw.message);
            setReqStatus(createPortalForm.isSuccess);
            setAlertMessage(parsedMsg);
            setAlertOpen(true);
            onOpenChange(false);
            createPortalForm.reset();
        }
    }, [createPortalForm]);

    useEffect(() => {
        if (updatePortalForm.isSuccess === true) {
            setReqStatus(updatePortalForm.isSuccess);
            setAlertMessage(`Form Updated Successfully!`);
            setAlertOpen(true);
            onOpenChange(false);
            updatePortalForm.reset();
        } else if (updatePortalForm.isError) {
            const raw = updatePortalForm.error as Error;
            const parsedMsg = parseApiErrorMessage(raw.message);
            setReqStatus(updatePortalForm.isSuccess);
            setAlertMessage(parsedMsg);
            setAlertOpen(true);
            onOpenChange(false);
            updatePortalForm.reset();
        }
    }, [updatePortalForm]);

    return (
        <>
            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContent className="max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>
                            {initialData ? "Edit Form" : "Create Form"}
                        </DialogTitle>
                    </DialogHeader>

                    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                        <div className="grid grid-cols-1 gap-4">

                            {!initialData && (
                                <div className="space-y-2">
                                    <Label htmlFor="ExistingFormId">Existing Form (Optional)</Label>
                                    <Controller
                                        name="ExistingFormId"
                                        control={control}
                                        render={({ field }) => (
                                            <Select
                                                value={field.value?.toString()}
                                                onValueChange={(val) => field.onChange(Number(val))}
                                            >
                                                <SelectTrigger id="ExistingFormId">
                                                    <SelectValue placeholder="Select Existing Form" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {existingForms?.items?.map((form: any) => (
                                                        <SelectItem key={form.Id} value={form.Id.toString()}>
                                                            {form.FormName}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        )}
                                    />
                                </div>
                            )}

                            <div className="space-y-1">
                                <Label htmlFor="FormPartnerType">Partner Type</Label>
                                <Controller
                                    name="FormPartnerType"
                                    control={control}
                                    render={({ field }) => (
                                        <Select
                                            value={field.value}
                                            onValueChange={(value) => field.onChange(value)}
                                        >
                                            <SelectTrigger id="FormPartnerType">
                                                <SelectValue placeholder="Select Partner Type" />
                                            </SelectTrigger>

                                            <SelectContent>
                                                {partners?.data?.items?.map((partner: any) => (
                                                    <SelectItem
                                                        key={partner.ID}
                                                        value={partner.ID.toString()}
                                                    >
                                                        {partner.PartnerName}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    )}
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="FormName">Form Title <span className="text-red-500">*</span></Label>
                                <Input
                                    id="FormName"
                                    placeholder="Form Title"
                                    {...register("FormName", { required: "Form Title is required" })}
                                />
                                {form.formState.errors.FormName && (
                                    <p className="text-red-500 text-xs">
                                        {form.formState.errors.FormName.message}
                                    </p>
                                )}
                            </div>

                        </div>

                        <div className="flex justify-end space-x-2">
                            <Button type="submit">
                                {initialData ?
                                    updatePortalForm.isPending ?
                                        (
                                            <>
                                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                                Updating...
                                            </>
                                        ) : (
                                            "Update"
                                        ) :
                                    createPortalForm.isPending ?
                                        (
                                            <>
                                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                                Saving...
                                            </>
                                        ) : (
                                            "Save"
                                        )}
                            </Button>
                            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                                Cancel
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>
            <AlertDialog
                open={alertOpen}
                onOpenChange={setAlertOpen}
                message={alertMessage}
                status={reqStatus}
            />
        </>
    );
}